import { setAccountContext, getPlanType } from '@/store'
import { currentUser } from '@/core/state'
import { collection, query, where, getDocs, doc, getDoc, orderBy, limit, getDocFromServer } from 'firebase/firestore' // <-- 1. IMPORT ADDED
import { db } from '@/core/firebase'
import { paths } from '@/db/paths'
import { BranchSchema, type Branch } from '@/db/schema/branch/branch'
import type { AccountSubscription, Account } from '@/db/schema'

const PLAN_RANK: Record<'free' | 'standard' | 'enterprise', number> = {
  free: 0,
  standard: 1,
  enterprise: 2,
}

async function fetchAccountPlan(accountId: string, userId: string): Promise<'free' | 'standard' | 'enterprise'> {
  try {
    const accountDoc = await getDoc(doc(db, paths.accounts(accountId).path))
    if (!accountDoc.exists()) return 'free'
    const accountData = accountDoc.data() as Account
    if (accountData.ownerUid !== userId) return 'free'

    const subscriptionsRef = collection(db, paths.accounts(accountId).subscriptions.collectionPath)
    const subsSnapshot = await getDocs(query(subscriptionsRef, orderBy('activatedAt', 'desc'), limit(1)))
    if (subsSnapshot.empty) return 'free'

    const subData = subsSnapshot.docs[0].data() as AccountSubscription
    const plan = subData.planType
    if (plan === 'enterprise' || plan === 'standard') {
      return plan
    }
    return 'free'
  } catch (error) {
    console.error('[ACCOUNT_INIT] Failed to fetch account plan:', error)
    return 'free'
  }
}

const STORAGE_KEY_BRANCH = 'selectedBranchId'

function getStoredBranchSelection(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY_BRANCH)
  } catch (error) {
    console.warn('[ACCOUNT_INIT] Failed to read from localStorage:', error)
    return null
  }
}

function storeSelectedBranch(branchId: string): void {
  try {
    localStorage.setItem(STORAGE_KEY_BRANCH, branchId)
  } catch (error) {
    console.warn('[ACCOUNT_INIT] Failed to write to localStorage:', error)
  }
}

function clearStoredBranchSelection(): void {
  try {
    localStorage.removeItem(STORAGE_KEY_BRANCH)
  } catch (error) {
    console.warn('[ACCOUNT_INIT] Failed to clear localStorage:', error)
  }
}

function selectDefaultBranch(branches: Branch[], storedSelection: string | null): string {
  if (branches.length === 1) {
    return branches[0]!.id
  }
  if (storedSelection) {
    const isValid = branches.some((b) => b.id === storedSelection)
    if (isValid) {
      return storedSelection
    } else {
      console.warn('[ACCOUNT_INIT] Stored branch selection invalid:', storedSelection)
      clearStoredBranchSelection()
    }
  }
  return branches[0]!.id
}

export async function initializeAccountContext(userId: string): Promise<void> {
  console.log('[ACCOUNT_INIT] Initializing account context for user:', userId)

  try {
    const membershipsQuery = query(
      collection(db, paths.memberships.collectionPath),
      where('userId', '==', userId),
    )
    const membershipsSnapshot = await getDocs(membershipsQuery)

    if (membershipsSnapshot.empty) {
      console.warn('[ACCOUNT_INIT] No memberships found for user. Setting minimal context.')
      setAccountContext({
        accountId: null,
        planType: 'free',
        role: null,
        userBrands: [],
        selectedBrandId: null,
        userBranches: [],
        selectedBranchId: null,
      })
      return
    }

    const membershipCount = membershipsSnapshot.size
    console.log(`[ACCOUNT_INIT] Found ${membershipCount} membership(s)`)

    const branchIds = membershipsSnapshot.docs.map((doc) => doc.data().branchId)
    const roleByBranch: Record<string, 'owner' | 'member'> = {}
    membershipsSnapshot.docs.forEach((doc) => {
      const m = doc.data() as { branchId?: string; role?: 'owner' | 'member' }
      const r: 'owner' | 'member' = m?.role ?? 'member'
      if (m?.branchId) roleByBranch[m.branchId] = r
    })
    const uniqueBranchIds = [...new Set(branchIds)]

    console.log(`[ACCOUNT_INIT] Fetching details for ${uniqueBranchIds.length} branch(es)`)

    const branchPromises = uniqueBranchIds.map(async (branchId) => {
      const branchDocRef = doc(db, paths.branches(branchId).path)
      
      // --- THIS IS THE FIX ---
      // Force a server read to bypass the stale cache.
      const branchSnap = await getDocFromServer(branchDocRef)
      // --- END FIX ---

      if (!branchSnap.exists()) {
        console.error(`[ACCOUNT_INIT] Branch not found: ${branchId}`)
        return null
      }

      const parseResult = BranchSchema.safeParse(branchSnap.data())
      if (!parseResult.success) {
        console.error(`[ACCOUNT_INIT] Invalid branch data for ${branchId}:`, parseResult.error)
        return null
      }
      return parseResult.data
    })

    const branchResults = await Promise.all(branchPromises)
    const branches = branchResults.filter((b): b is Branch => b !== null)

    if (branches.length === 0) {
      console.error('[ACCOUNT_INIT] No valid branches found. Setting minimal context.')
      setAccountContext({
        accountId: null,
        planType: 'free',
        role: null,
        userBrands: [],
        selectedBrandId: null,
        userBranches: [],
        selectedBranchId: null,
      })
      return
    }

    console.log(`[ACCOUNT_INIT] Successfully fetched ${branches.length} branch(es)`)

    const storedSelection = getStoredBranchSelection()
    const selectedBranchId = selectDefaultBranch(branches, storedSelection)

    const ownedBranches = branches.filter((branch) => roleByBranch[branch.id] === 'owner')
    const ownedAccountIds = Array.from(new Set(ownedBranches.map((branch) => branch.accountId)))
    const primaryAccountId = branches.find((branch) => branch.id === selectedBranchId)?.accountId
      || ownedBranches[0]?.accountId
      || branches[0]!.accountId
    console.log(`[ACCOUNT_INIT] Using accountId: ${primaryAccountId}`)

    let branchBasedPlan: 'free' | 'standard' | 'enterprise' = 'free'
    if (ownedAccountIds.length > 0) {
      for (const accountId of ownedAccountIds) {
        const accountPlan = await fetchAccountPlan(accountId, userId)
        if (PLAN_RANK[accountPlan] > PLAN_RANK[branchBasedPlan]) {
          branchBasedPlan = accountPlan
        }
      }
    }
    console.log(`[ACCOUNT_INIT] Determined planType: ${branchBasedPlan}`)

    let userBrands: { brandId: string, brandName: string }[] = []
    let selectedBrandId: string | null = null
    if (primaryAccountId) {
      const brandsRef = collection(db, paths.brands.collectionPath)
      const brandsQuery = query(brandsRef, where('accountId', '==', primaryAccountId))
      const brandsSnapshot = await getDocs(brandsQuery)
      userBrands = brandsSnapshot.docs.map((doc) => ({
        brandId: doc.id,
        brandName: doc.data().brandName || 'Unnamed Brand',
      })).sort((a, b) => a.brandName.localeCompare(b.brandName))

      let storedBrand: string | null = null
      try { storedBrand = localStorage.getItem('selectedBrandId') } catch (_e) { /* ignore */ }
      selectedBrandId = (storedBrand && userBrands.find(b => b.brandId === storedBrand))
        ? storedBrand
        : (userBrands[0]?.brandId || null)
    }

    console.log(`[ACCOUNT_INIT] Selected branchId: ${selectedBranchId}`)
    storeSelectedBranch(selectedBranchId)

    setAccountContext({
      accountId: primaryAccountId,
      planType: branchBasedPlan,
      role: roleByBranch[selectedBranchId] || 'member',
      userBrands: userBrands,
      selectedBrandId: selectedBrandId,
      userBranches: branches.map((b) => ({
        branchId: b.id,
        branchName: b.branchName || b.id,
        role: roleByBranch[b.id] || 'member',
        isDefaultName: b.isDefaultName === false ? false : true,
        accountId: b.accountId,
        brandId: b.brandId,
      })),
      selectedBranchId,
    })

    console.log('[ACCOUNT_INIT] Account context initialized successfully')
  } catch (error) {
    console.error('[ACCOUNT_INIT] Error initializing account context:', error)
    setAccountContext({
      accountId: null,
      planType: 'free',
      role: null,
      userBrands: [],
      selectedBrandId: null,
      userBranches: [],
      selectedBranchId: null,
    })
  }
}

export function initAccountOnAppLoad(): void {
  if (!currentUser) {
    console.warn('[ACCOUNT_INIT] No authenticated user, skipping account init')
    return
  }
  initializeAccountContext(currentUser.uid).catch((error) => {
    console.error('[ACCOUNT_INIT] Unhandled error in initialization:', error)
  })
}